<template>
    <div class="ai-create-container">
        <!-- 搜索区域 -->
        <div class="search-bar">
            <el-input v-model="searchForm.title_keyword" placeholder="请输入关键词" clearable size="large"
                style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
                <template #prefix>
                    <el-icon><Search /></el-icon>
                </template>
            </el-input>

            <el-button type="primary" size="large" @click="handleSearch">
                <el-icon><Search /></el-icon>
                搜索
            </el-button>

            <el-button type="success" size="large" @click="openCreateDialog" style="float: right">
                <el-icon><Plus /></el-icon>
                新增AI任务
            </el-button>
        </div>

        <!-- AI任务列表 -->
        <el-table :data="taskList" stripe style="width: 100%; margin-top: 20px">
            <el-table-column prop="title_keyword" label="关键词" width="200" />
            <el-table-column prop="status" label="状态" width="120">
                <template #default="{ row }">
                    <el-tag :type="getStatusType(row.status)">
                        {{ getStatusText(row.status) }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="notes" label="备注" width="200" />
            <el-table-column prop="create_time" label="创建时间" />

            <el-table-column label="操作" width="250" fixed="right">
                <template #default="scope">
                    <el-button type="info" size="small" @click="openTitleListDialog(scope.row)">
                        <el-icon><View /></el-icon>
                        查看标题
                    </el-button>
                    <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
                        <el-icon><Delete /></el-icon>
                        删除
                    </el-button>
                </template>
            </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-container">
            <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                :total="pagination.total" :page-sizes="[10, 20, 30, 50]"
                layout="total, sizes, prev, pager, next, jumper" @size-change="fetchTaskList"
                @current-change="fetchTaskList" />
        </div>

        <!-- 新增AI任务对话框 -->
        <el-dialog v-model="createDialogVisible" title="新增AI任务" width="600px"
            :close-on-click-modal="false" :append-to-body="true" class="child-dialog">
            <el-form :model="createForm" label-width="120px">
                <el-form-item label="关键词" prop="title_keyword">
                    <el-input v-model="createForm.title_keyword" placeholder="请输入关键词" size="large" />
                </el-form-item>
                <el-form-item label="备注">
                    <el-input v-model="createForm.notes" placeholder="请输入备注" type="textarea" :rows="3" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="createDialogVisible = false">取消</el-button>
                <el-button type="primary" @click="submitCreateForm">确认</el-button>
            </template>
        </el-dialog>

        <!-- 查看标题对话框 -->
        <el-dialog v-model="titleListDialogVisible" :title="titleListDialogTitle" width="80%"
            top="5vh" :close-on-click-modal="false" :lock-scroll="false"
            :append-to-body="true" class="child-dialog">
            <TitleListByTaskView v-if="titleListDialogVisible" :task-id="currentTaskId" :store-id="storeId" />
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import TitleListByTaskView from './TitleListByTaskView.vue';
import { Search, Plus, View, Delete } from '@element-plus/icons-vue';

export default {
    components: {
        TitleListByTaskView,
        Search, Plus, View, Delete
    },
    props: {
        titleId: {
            type: Number,
            required: true
        },
        storeId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            loading: false,
            searchForm: {
                title_keyword: ''
            },
            taskList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },
            createDialogVisible: false,
            createForm: {
                title_keyword: '',
                notes: ''
            },
            titleListDialogVisible: false,
            titleListDialogTitle: '',
            currentTaskId: null
        }
    },
    mounted() {
        this.fetchTaskList();
    },
    methods: {
        getStatusType(status) {
            const map = {
                1: 'danger',
                2: 'success',
                3: 'warning'
            };
            return map[status] || '';
        },

        getStatusText(status) {
            const map = {
                1: '失败',
                2: '成功',
                3: '进行中'
            };
            return map[status] || '';
        },

        async fetchTaskList() {
            this.loading = true;
            try {
                const params = {
                    action: this.searchForm.title_keyword ? 'search' : 'list',
                    store_id: this.storeId,
                    title_id: this.titleId,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                };

                if (this.searchForm.title_keyword) {
                    params.title_keyword = this.searchForm.title_keyword;
                }

                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/title_ai_create.php`,
                    params
                );

                if (data.data) {
                    this.taskList = data.data;
                    this.pagination.total = data.pagination?.total || data.data.length;
                }
            } catch (error) {
                this.$message.error('获取AI任务列表失败');
                console.error('获取AI任务列表错误:', error);
            } finally {
                this.loading = false;
            }
        },

        handleSearch() {
            this.pagination.page = 1;
            this.fetchTaskList();
        },

        openCreateDialog() {
            this.createForm = {
                title_keyword: '',
                status: 3,
                notes: ''
            };
            this.createDialogVisible = true;
        },

        async submitCreateForm() {
            try {
                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/title_ai_create.php`,
                    {
                        action: 'add',
                        store_id: this.storeId,
                        title_id: this.titleId,
                        ...this.createForm
                    }
                );

                if (data.success) {
                    this.$message.success('AI任务创建成功');
                    this.createDialogVisible = false;
                    this.fetchTaskList();
                } else {
                    this.$message.error(data.error || '创建失败');
                }
            } catch (error) {
                this.$message.error('创建失败');
                console.error('创建AI任务错误:', error);
            }
        },

        openTitleListDialog(row) {
            this.currentTaskId = row.id;
            this.titleListDialogTitle = `${row.title_keyword} - 生成标题`;
            this.titleListDialogVisible = true;
        },

        async handleDelete(id) {
            this.$confirm('确定要删除该AI任务吗？', '警告', {
                confirmButtonText: '确认删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(async () => {
                try {
                    const { data } = await axios.post(
                        `${config.BASE_URL}/shop/api/title_ai_create.php`,
                        {
                            action: 'delete',
                            id: id,
                            store_id: this.storeId
                        }
                    );

                    if (data.success) {
                        this.$message.success('删除成功');
                        this.fetchTaskList();
                    } else {
                        this.$message.error(data.error || '删除失败');
                    }
                } catch (error) {
                    this.$message.error('删除失败');
                    console.error('删除AI任务错误:', error);
                }
            }).catch(() => { });
        }
    }
}
</script>

<style scoped>
.ai-create-container {
    padding: 20px;
    background: transparent !important;
    color: #e0e0e0;
}

/* 关键修改：search-bar 不要左右padding，让它和表格对齐 */
.search-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    margin-bottom: 20px;
    padding: 16px 0;  /* 只保留上下padding，去掉左右padding */
    background: transparent !important;  /* 去掉背景，或者改为和表格一样的透明 */
    border: none;  /* 去掉边框 */
}

/* 如果要保留 search-bar 的背景卡片效果，用这个包裹方式 */
.search-bar-wrapper {
    padding: 16px;
    background: rgba(18, 14, 33, 0.4);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    border: 1px solid rgba(157, 78, 221, 0.2);
    margin-bottom: 20px;
}

.pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
}
</style>